import { Fragment, ReactNode } from 'react';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import Chevron from '../../../assets/icons/General/chevron.svg?react';

type OptionRenderState = {
  active: boolean;
  selected: boolean;
  disabled: boolean;
};

export type SelectOption<T> = {
  label: string;
  value: T;
  key?: string | number;
  disabled?: boolean;
  description?: ReactNode;
  prefix?: ReactNode;
};

export type SelectProps<T> = {
  value: T | null;
  onChange: (value: T, option: SelectOption<T>) => void;
  options: SelectOption<T>[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  dropdownClassName?: string;
  dropdownMinWidth?: number | string;
  optionClassName?:
    | string
    | ((state: OptionRenderState, option: SelectOption<T>) => string);
  renderDisplay?: (option: SelectOption<T> | null) => ReactNode;
  renderOption?: (
    option: SelectOption<T>,
    state: OptionRenderState,
  ) => ReactNode;
  chevronIcon?: ReactNode;
  isOptionEqual?: (optionValue: T, currentValue: T) => boolean;
  name?: string;
};

const defaultButtonClasses =
  'flex flex-row bg-white border border-grey-100 rounded-lg justify-between items-center gap-1 text-grey-800 px-3 py-2 min-w-[15rem]';

const defaultDropdownClasses =
  'absolute left-0 z-10 mt-1 max-h-[300px] w-fit origin-top overflow-auto rounded-lg border border-black-200 bg-white py-1 text-left shadow-lg focus:outline-none';

const getBaseOptionClasses = (state: OptionRenderState, disabled?: boolean) =>
  twMerge(
    'flex cursor-pointer items-center gap-3 px-[14px] py-[10px] text-base transition',
    state.selected && 'bg-black-200 text-black-900',
    state.active && 'bg-black-100 text-black-900',
    disabled && 'cursor-not-allowed text-black-400',
  );

const Select = <T,>({
  value,
  onChange,
  options,
  placeholder = 'Select',
  disabled = false,
  className,
  buttonClassName,
  dropdownClassName,
  dropdownMinWidth,
  optionClassName,
  renderDisplay,
  renderOption,
  chevronIcon,
  isOptionEqual,
  name,
}: SelectProps<T>) => {
  const matcher =
    isOptionEqual ??
    ((optionValue: T, currentValue: T) => optionValue === currentValue);

  const selectedOption =
    value === null
      ? null
      : (options.find((option) => matcher(option.value, value)) ?? null);

  const resolvedDropdownMinWidth = (() => {
    if (typeof dropdownMinWidth === 'number') {
      return `${dropdownMinWidth}px`;
    }

    if (typeof dropdownMinWidth === 'string' && dropdownMinWidth.trim()) {
      return dropdownMinWidth;
    }

    return '14rem';
  })();

  return (
    <Listbox
      value={selectedOption}
      onChange={(option) => {
        if (option) {
          onChange(option.value, option);
        }
      }}
      disabled={disabled}
    >
      {({ open }) => (
        <div
          className={twMerge('relative', className, disabled && 'opacity-60')}
        >
          {name && (
            <input
              type="hidden"
              name={name}
              value={selectedOption ? String(selectedOption.value) : ''}
            />
          )}
          <ListboxButton
            className={twMerge(defaultButtonClasses, buttonClassName)}
          >
            <span className="block truncate">
              {renderDisplay?.(selectedOption) ??
                selectedOption?.label ??
                placeholder}
            </span>
            <span className="ml-2 flex h-5 w-5 items-center justify-center gap-4">
              {chevronIcon ?? (
                <Chevron
                  className={twMerge(
                    'stroke-grey-500 h-4 w-4 transition-transform duration-200 ease-out',
                    open && 'rotate-180',
                  )}
                  aria-hidden
                />
              )}
            </span>
          </ListboxButton>

          <Transition
            as={Fragment}
            enter="transition duration-150 ease-out"
            enterFrom="opacity-0 -translate-y-1 scale-95"
            enterTo="opacity-100 translate-y-0 scale-100"
            leave="transition duration-125 ease-in"
            leaveFrom="opacity-100 translate-y-0 scale-100"
            leaveTo="opacity-0 -translate-y-1 scale-95"
          >
            <ListboxOptions
              className={twMerge(defaultDropdownClasses, dropdownClassName)}
              style={{ minWidth: `max(${resolvedDropdownMinWidth}, 100%)` }}
            >
              {options.map((option) => (
                <ListboxOption
                  key={option.key ?? String(option.value)}
                  value={option}
                  disabled={option.disabled}
                  className={({
                    active,
                    selected,
                    disabled: optionDisabled,
                  }) => {
                    const state = {
                      active,
                      selected,
                      disabled: !!optionDisabled,
                    };

                    return typeof optionClassName === 'function'
                      ? twMerge(
                          getBaseOptionClasses(state, option.disabled),
                          optionClassName(state, option),
                        )
                      : twMerge(
                          getBaseOptionClasses(state, option.disabled),
                          optionClassName,
                        );
                  }}
                >
                  {({ active, selected, disabled: optionDisabled }) => (
                    <div className="flex w-full items-center gap-3">
                      {option.prefix}
                      <div className="flex-1">
                        {renderOption?.(option, {
                          active,
                          selected,
                          disabled: !!optionDisabled,
                        }) ?? (
                          <span className="block truncate">{option.label}</span>
                        )}
                        {option.description && (
                          <span className="text-black-500 mt-0.5 block text-xs">
                            {option.description}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
export default Select;
